import {Box, Typography, Stack} from '@mui/material'
import {withTranslation, WithTranslation} from 'react-i18next'
import TimelineEventTemplate from 'web-common/views/timeline/TimelineEventTemplate'
import {AssistantEventProducer} from '../AssistantEventProducer'
import {ChatAdapter} from '@fnd/timeline'

interface ChatSuggestionsProps {
  producer: AssistantEventProducer
}

interface InternalProps extends ChatSuggestionsProps, WithTranslation {}

interface ChatSuggestionsState {
  shuffled1: string[]
  shuffled2: string[]
}

class ChatSuggestions extends TimelineEventTemplate<
  InternalProps,
  ChatSuggestionsState
> {
  private NUMBER_OF_SUGGESTIONS = 27

  constructor(props: InternalProps) {
    super(props)
    this.state = {
      shuffled1: [],
      shuffled2: []
    }
  }

  componentDidMount() {
    const [shuffled1, shuffled2] = this.getShuffledSuggestions()
    this.setState({shuffled1, shuffled2})
    this.props.i18n.on('languageChanged', this.handleLanguageChanged)
  }

  componentWillUnmount() {
    this.props.i18n.off('languageChanged', this.handleLanguageChanged)
  }

  handleLanguageChanged = () => {
    const [shuffled1, shuffled2] = this.getShuffledSuggestions()
    this.setState({shuffled1, shuffled2})
  }

  shuffle<T>(array: T[]): T[] {
    const result = [...array]
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[result[i], result[j]] = [result[j], result[i]]
    }
    return result
  }

  get chatSuggestions(): string[] {
    return Array.from({length: this.NUMBER_OF_SUGGESTIONS}, (_, i) =>
      this.props.t(`fnd-assistant-action-suggestions-${i + 1}`)
    )
  }

  getShuffledSuggestions(): [string[], string[]] {
    const fullShuffled = this.shuffle(this.chatSuggestions)
    const mid = Math.ceil(fullShuffled.length / 2)
    return [fullShuffled.slice(0, mid), fullShuffled.slice(mid)]
  }

  handleOnSuggestionClick = async (suggestion: string) => {
    await this.props.producer.createEvent({
      message: suggestion,
      adapterId: ChatAdapter.id
    })

    this.props.producer.actionManager.removeChatSuggestions()
  }

  renderSuggestionsRow = (
    suggestions: string[],
    animation: string,
    reverse?: boolean
  ) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        maskImage:
          'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
        overflow: 'hidden',
        marginY: 1
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          animation: `${animation} 100s linear infinite`,
          flex: 'none',
          ...(reverse && {
            animationDirection: 'reverse'
          })
        }}
      >
        {[...suggestions, ...suggestions].map((suggestion, index) => (
          <Box
            key={`${animation}-${index}`}
            component="button"
            onClick={() => this.handleOnSuggestionClick(suggestion)}
            sx={{
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 50,
              paddingX: 2,
              paddingY: 0.5,
              fontSize: '1rem',
              color: 'primary.main',
              backgroundColor: 'white',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white'
              }
            }}
          >
            {suggestion}
          </Box>
        ))}
      </Box>
    </Box>
  )

  render() {
    const {shuffled1, shuffled2} = this.state
    const dir = this.props.i18n.dir()
    const animationName = `scrollSuggestionsAnimation-${dir}`

    return (
      <Stack spacing={2} alignItems="center" width="100%">
        <Typography variant="h6" color="textSecondary" align="center">
          {this.props.t('fnd-assistant-action-suggestions-title')}
        </Typography>
        <Box sx={{overflow: 'hidden', paddingY: 1, width: '100%'}}>
          <Box sx={{marginX: -2, paddingX: 2}}>
            {this.renderSuggestionsRow(shuffled1, animationName)}
            {this.renderSuggestionsRow(shuffled2, animationName, true)}
          </Box>
        </Box>
        <style>
          {`
            @keyframes scrollSuggestionsAnimation-ltr {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }

            @keyframes scrollSuggestionsAnimation-rtl {
              from { transform: translateX(0); }
              to { transform: translateX(50%); }
            }
          `}
        </style>
      </Stack>
    )
  }
}

export default withTranslation()(ChatSuggestions)
